import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import { Bedtime, CheckCircle, ErrorOutline, ExpandLess, ExpandMore, Schedule } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { Section } from '../analytics/owner/parts';
import { INK, SERIES } from '../analytics/chartTheme';
import { Panel } from '../analytics/DashboardKit';
import { formatClock } from '../analytics/owner/ownerData';
import {
  agingBuckets,
  contactName,
  customerName,
  inChannel,
  openByCustomer,
  whenSgt,
  type AgeBucket,
  type CustomerQueue,
  type OpenRfq,
  type OpsHealth,
  type QueueChannel,
  type QueueState,
  type QueueStatus,
} from './opsHealth';

// The Live Ops tab's open-RFQ queue: channel and wait filters, the per-customer
// table, and the oldest-first list (LiveOpsTab computes the rows).

const QUEUE_PAGE = 15;
const nf = (n: number | null | undefined) => (n == null ? '-' : n.toLocaleString('en-SG'));

const STATE_CHIP: Record<QueueState, { label: string; color: 'success' | 'warning' | 'error' | 'default'; icon: React.ReactElement }> = {
  within: { label: 'Within 4 h', color: 'success', icon: <CheckCircle fontSize="small" /> },
  due_soon: { label: 'Due soon', color: 'warning', icon: <Schedule fontSize="small" /> },
  overdue: { label: 'Over 4 h', color: 'error', icon: <ErrorOutline fontSize="small" /> },
  after_hours: { label: 'After hours', color: 'default', icon: <Bedtime fontSize="small" /> },
};

export type QueueRow = OpenRfq & QueueStatus & { customer: string; customerLabel: string; bucket: AgeBucket };

const PortalTag: React.FC = () => (
  <MuiTooltip title="Wrist, Seven Seas and ARC Marine quote mostly through their own portals">
    <Chip size="small" label="Portal" variant="outlined" sx={{ height: 20, fontSize: 12, ml: 0.75, verticalAlign: 'middle' }} />
  </MuiTooltip>
);

const CustomerCell: React.FC<{ row: QueueRow }> = ({ row }) => {
  const person = contactName(row);
  return (
    <>
      <Typography variant="body2" component="div" sx={{ fontWeight: 500 }}>
        {row.customerLabel}
        {row.portal && <PortalTag />}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', display: 'block' }}>
        {[person, row.client_email ?? (row.customerLabel === row.customer ? null : row.customer)].filter(Boolean).join(' · ')}
      </Typography>
    </>
  );
};

const QueueTable: React.FC<{ rows: QueueRow[]; manual?: boolean; caption: string }> = ({ rows, manual, caption }) => (
  <TableContainer>
    <Table size="small" aria-label={caption}>
      <TableHead>
        <TableRow>
          <TableCell>{manual ? 'Since arrival' : 'Waiting'}</TableCell>
          <TableCell>Customer</TableCell>
          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Subject</TableCell>
          <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
            Items
          </TableCell>
          <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
            <Box component="span" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
              Open
            </Box>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => {
          const chip = STATE_CHIP[r.state];
          const link = `/?tab=${r.status}&rfq=${r.rfq_id}`;
          return (
            <TableRow key={r.rfq_id} hover>
              <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                <Stack spacing={0.5} alignItems="flex-start">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatClock(r.waited)}
                  </Typography>
                  {!manual && (
                    <MuiTooltip
                      title={
                        r.state === 'after_hours'
                          ? `Arrived outside office hours; its 4 hours start ${whenSgt(r.clockStarts)}`
                          : r.arrivedAfterHours
                            ? `Arrived after hours; ${formatClock(r.slaWaited)} since the office opened`
                            : `${formatClock(r.slaWaited)} of the 4-hour target`
                      }
                    >
                      <Chip size="small" variant="outlined" color={chip.color} icon={chip.icon} label={chip.label} />
                    </MuiTooltip>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    since {whenSgt(r.received_at)}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>
                <CustomerCell row={r} />
                <Typography variant="caption" sx={{ display: { xs: 'block', md: 'none' }, wordBreak: 'break-word', mt: 0.5 }}>
                  {r.subject}
                </Typography>
                <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.5 }}>
                  <Button size="small" variant="outlined" component={RouterLink} to={link} aria-label={`Open RFQ ${r.rfq_id}`}>
                    Open
                  </Button>
                </Box>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, maxWidth: 420, verticalAlign: 'top' }}>
                <Typography variant="body2" noWrap title={r.subject ?? ''}>
                  {r.subject || '-'}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, verticalAlign: 'top' }}>
                {r.item_count}
              </TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, verticalAlign: 'top' }}>
                <Button size="small" variant="outlined" component={RouterLink} to={link} aria-label={`Open RFQ ${r.rfq_id}`}>
                  Open
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

/**
 * Open RFQs per wait bucket, drawn as buttons: each bar filters the list and
 * is reachable by keyboard, with its state announced (aria-pressed).
 */
const AgeFilter: React.FC<{ buckets: Array<{ key: AgeBucket; label: string; n: number }>; value: AgeBucket | null; onChange: (b: AgeBucket | null) => void }> = ({
  buckets,
  value,
  onChange,
}) => {
  const max = Math.max(1, ...buckets.map((b) => b.n));
  return (
    <Box role="group" aria-label="Filter the list by how long the RFQ has waited" sx={{ display: 'grid', gridTemplateColumns: `repeat(${buckets.length}, 1fr)`, gap: 1, alignItems: 'end', height: 168 }}>
      {buckets.map((b) => {
        const selected = value === b.key;
        const dimmed = value != null && !selected;
        return (
          <ButtonBase
            key={b.key}
            aria-pressed={selected}
            aria-label={`${b.label}: ${b.n} open RFQ${b.n === 1 ? '' : 's'}`}
            onClick={() => onChange(selected ? null : b.key)}
            sx={{
              flexDirection: 'column',
              justifyContent: 'flex-end',
              height: '100%',
              borderRadius: 1,
              px: 0.5,
              pb: 0.5,
              outline: selected ? `2px solid ${SERIES.system}` : 'none',
              '&:hover': { bgcolor: alpha(SERIES.system, 0.06) },
              '&.Mui-focusVisible': { outline: `2px solid ${INK.primary}` },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {b.n}
            </Typography>
            <Box sx={{ width: '70%', maxWidth: 44, height: `${Math.max(2, (b.n / max) * 100)}px`, bgcolor: SERIES.system, opacity: dimmed ? 0.35 : 1, borderRadius: '4px 4px 0 0' }} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.2, textAlign: 'center' }}>
              {b.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

/** Open RFQs per customer; selecting one filters the list. */
function CustomersPanel({
  customers,
  selected,
  onSelect,
}: {
  customers: CustomerQueue[];
  selected: string | null;
  onSelect: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  return (
    <Panel title="By customer" subtitle="Select a customer to filter the list.">
      {customers.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          Nothing waiting.
        </Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 260 }}>
          <Table size="small" stickyHeader aria-label="Open RFQs by customer">
            <TableHead>
              <TableRow>
                <TableCell sx={{ pl: 0 }}>Customer</TableCell>
                <TableCell align="right">Open</TableCell>
                <TableCell align="right">Over 4 h</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  Waiting since
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((c) => (
                <TableRow key={c.customer} hover selected={selected === c.customer}>
                  <TableCell sx={{ pl: 0 }}>
                    <ButtonBase
                      aria-pressed={selected === c.customer}
                      onClick={() => onSelect((x) => (x === c.customer ? null : c.customer))}
                      sx={{ font: 'inherit', color: 'primary.main', textDecoration: 'underline', textUnderlineOffset: 3, textAlign: 'left', borderRadius: 0.5 }}
                    >
                      {c.name}
                    </ButtonBase>
                    {c.portal && <PortalTag />}
                  </TableCell>
                  <TableCell align="right">{c.open}</TableCell>
                  <TableCell align="right" sx={{ color: c.overdue ? 'error.main' : undefined }}>
                    {c.overdue}
                  </TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                    {whenSgt(c.oldest)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Panel>
  );
}

interface OpenQueueSectionProps {
  data: OpsHealth;
  /** Status NEW, oldest first. */
  open: QueueRow[];
  /** Moved to manual handling (status DIY). */
  manual: QueueRow[];
  backlog: QueueRow[];
  emailCount: number;
  portalCount: number;
}

/** "Open RFQs to quote": filters, the wait buckets, the customers and the list. */
export function OpenQueueSection({ data, open, manual, backlog, emailCount, portalCount }: OpenQueueSectionProps) {
  const [showManual, setShowManual] = useState(false);
  const [channel, setChannel] = useState<QueueChannel>('all');
  const [bucket, setBucket] = useState<AgeBucket | null>(null);
  const [customer, setCustomer] = useState<string | null>(null);
  const [allQueue, setAllQueue] = useState(false);

  const inView = open.filter((r) => inChannel(r, channel));
  const buckets = agingBuckets(inView.map((r) => r.waited));
  const customers = openByCustomer(inView);
  const shown = inView.filter((r) => (!bucket || r.bucket === bucket) && (!customer || r.customer === customer));
  const listCapped = data.open_rfqs.count > open.length;
  const oldestBacklog = backlog.reduce<QueueRow | null>((a, r) => (!a || r.received_at < a.received_at ? r : a), null);
  const filtered = bucket || customer;

  return (
    <Section
      id="live-queue"
      title="Open RFQs to quote"
      subtitle="Waiting time is clock time since the RFQ arrived. Target: first reply within 4 hours. An RFQ that arrives outside Mon-Fri 09:00-18:00 is shown as after hours until the office opens; its 4 hours then run from 09:00. Public holidays are not excluded."
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={channel}
          onChange={(_, v: QueueChannel | null) => {
            if (!v) return;
            setChannel(v);
            setCustomer(null);
          }}
          aria-label="Show RFQs from"
        >
          <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.5 }}>
            All ({nf(data.open_rfqs.count)})
          </ToggleButton>
          <ToggleButton value="email" sx={{ textTransform: 'none', px: 1.5 }}>
            By email ({nf(emailCount)})
          </ToggleButton>
          <ToggleButton value="portal" sx={{ textTransform: 'none', px: 1.5 }}>
            Portal customers ({nf(portalCount)})
          </ToggleButton>
        </ToggleButtonGroup>
        {bucket && <Chip size="small" label={`Waited ${buckets.find((b) => b.key === bucket)?.label}`} onDelete={() => setBucket(null)} />}
        {customer && <Chip size="small" label={customers.find((c) => c.customer === customer)?.name ?? customerName(customer)} onDelete={() => setCustomer(null)} />}
        {filtered && (
          <Button
            size="small"
            onClick={() => {
              setBucket(null);
              setCustomer(null);
            }}
          >
            Clear filters
          </Button>
        )}
      </Stack>
      {channel === 'portal' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Wrist, Seven Seas and ARC Marine quote mostly through their own portals, so an RFQ of theirs can be answered there and still show here
          until it is marked in the system.
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={5}>
          <Panel title="How long they have waited" subtitle="Clock time since arrival. Select a bar to filter the list.">
            <AgeFilter buckets={buckets} value={bucket} onChange={setBucket} />
            {backlog.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                <Bedtime fontSize="small" sx={{ mt: '2px' }} />
                {backlog.length} arrived after hours and wait for the morning; their 4 hours start {whenSgt(oldestBacklog?.clockStarts ?? null)}.
              </Typography>
            )}
          </Panel>
        </Grid>
        <Grid item xs={12} md={7}>
          <CustomersPanel customers={customers} selected={customer} onSelect={setCustomer} />
        </Grid>
      </Grid>

      <Panel sx={{ height: 'auto' }} title={`Oldest first${shown.length !== open.length ? ` · ${shown.length} of ${open.length} shown` : ''}`}>
        {listCapped && (
          <Typography variant="body2" color="text.secondary">
            The oldest {open.length} of {nf(data.open_rfqs.count)} open RFQs are listed; the counts above cover all of them.
          </Typography>
        )}
        {shown.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            {open.length === 0 ? 'Nothing waiting - every RFQ has been quoted.' : 'No open RFQs match the filter.'}
          </Typography>
        ) : (
          <>
            <QueueTable rows={allQueue ? shown : shown.slice(0, QUEUE_PAGE)} caption="Open RFQs, oldest first" />
            {shown.length > QUEUE_PAGE && (
              <Button size="small" onClick={() => setAllQueue((v) => !v)} endIcon={allQueue ? <ExpandLess /> : <ExpandMore />} sx={{ mt: 1 }} aria-expanded={allQueue}>
                {allQueue ? `Show the oldest ${QUEUE_PAGE} only` : `Show all ${shown.length}`}
              </Button>
            )}
          </>
        )}
        {manual.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Button size="small" onClick={() => setShowManual((v) => !v)} endIcon={showManual ? <ExpandLess /> : <ExpandMore />} aria-expanded={showManual}>
              {nf(data.open_rfqs.manual_count)} moved to manual handling (not counted above)
            </Button>
            <Collapse in={showManual}>
              <QueueTable rows={manual} manual caption="RFQs moved to manual handling" />
            </Collapse>
          </Box>
        )}
      </Panel>
    </Section>
  );
}
